import { useEffect, useRef, useState } from "react";
import { useConnect, useConnectors } from "wagmi";
import { Button } from "../components/ui";
import { hasInjectedWallet, isMobile, walletApps } from "./apps";

/**
 * Connects an injected wallet: straight to it when there is one, a short list when there are more.
 * On a phone, whose browser has no wallet, it offers to open the page in a wallet app instead.
 */
export function ConnectButton({ size = "md", block = false, label = "Connect" }: { size?: "sm" | "md" | "lg"; block?: boolean; label?: string }) {
  const connectors = useConnectors();
  const { mutate: connect, isPending } = useConnect();
  const [picking, setPicking] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const announced = connectors.filter((connector) => connector.id !== "injected");
  const choices = announced.length > 0 ? announced : connectors;
  const inApp = isMobile() && announced.length === 0 && !hasInjectedWallet();

  useEffect(() => {
    if (!picking) return;
    const close = (event: PointerEvent) => {
      if (!ref.current?.contains(event.target as Node)) setPicking(false);
    };
    window.addEventListener("pointerdown", close);
    return () => window.removeEventListener("pointerdown", close);
  }, [picking]);

  return (
    <div className={`connect-wrap ${block ? "connect-block" : ""}`} ref={ref}>
      <Button
        tone="primary"
        size={size}
        block={block}
        busy={isPending}
        onClick={() => {
          const only = choices[0];
          if (!inApp && choices.length === 1 && only) connect({ connector: only });
          else setPicking((open) => !open);
        }}
      >
        {isPending ? "Connecting" : label}
      </Button>
      {picking && inApp && (
        <ul className="wallet-menu" role="menu">
          <li className="wallet-menu-empty">Open this page in your wallet's app to connect.</li>
          {walletApps.map((app) => (
            <li key={app.name}>
              <a role="menuitem" href={app.link(window.location.href)} onClick={() => setPicking(false)}>
                <span className="wallet-blank wallet-mark">{app.name.charAt(0)}</span>
                {app.name}
              </a>
            </li>
          ))}
        </ul>
      )}
      {picking && !inApp && (
        <ul className="wallet-menu" role="menu">
          {choices.length === 0 && (
            <li className="wallet-menu-empty">
              No browser wallet found. Install one such as Rabby or MetaMask, then reload.
            </li>
          )}
          {choices.map((connector) => (
            <li key={connector.uid}>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setPicking(false);
                  connect({ connector });
                }}
              >
                {connector.icon ? <img src={connector.icon} alt="" width={20} height={20} /> : <span className="wallet-blank" />}
                {connector.name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
